import type { Transition } from "@uirouter/react";

import { logWithTransitionScope } from "app/sentry/utils/logWithTransitionScope";
import { chosenItems } from "app/common/services/chosenItems";
import { swSettings } from "app/common/services/swSettings";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { ERankingCategoryMode } from "app/pages/app-intelligence/app-analysis/pages/Ranking/types";
import { APPLE_STORE, AppStoreID } from "app/pages/app-intelligence/constants";
import { getHasPremiumPlusClaim, getIsNewEngagementIosMetrics } from "app/pages/app-intelligence/helpers";
import type { SecondaryBarType } from "app/reducers/_reducers/SecondaryBarReducer";
import { resolvers } from "single-spa/react-app/config/resolvers/resolvers";
import type { IRouterState, RouterStateConfig } from "single-spa/react-app/router/IRouterState";

const hasPremiumPlusClaim = getHasPremiumPlusClaim();

const swConfig: RouterStateConfig = {
    digitalsuite_appanalysis_home: {
        parent: "digitalsuite_marketresearch",
        url: "/companyresearch/appanalysis/home",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_appanalysis_home",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        configId: "AppAnalysisHome",
        configIdDeps: ["AppPerformanceReport", "AppRetentionFront", "AppAudienceInterests"],
        secondaryBarType: "DigitalSuiteCompetition" as SecondaryBarType,
        secondaryBarType3: SecondarySideBarType3.None,
        isSecondaryBarOpen: true,
        pageId: {
            section: "marketresearch",
            subSection: "companyresearch",
            subSubSection: "analyzeapps",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Home",
            subSubSection: "Analyze Apps",
        },
        pageTitle: "companyintelligence.analyzeapps.homepage.title",
    },
    digitalsuite_app: {
        parent: "digitalsuite_marketresearch_root",
        abstract: true,
        url: "/companyresearch/appanalysis",
        configId: "AppAnalysis",
        secondaryBarType: "DigitalSuiteCompetition" as SecondaryBarType,
        secondaryBarType3: SecondarySideBarType3.AppAnalysis,
        data: {
            menuId: "apps",
            menuKbItems(state: any) {
                let section;
                if (state.name.indexof("apps-ranking") > 0) {
                    section = "overview";
                } else {
                    switch (state.parent) {
                        case "apps.storepageanalysis":
                            section = "storepage";
                            break;
                        case "apps.engagement":
                            section = "engagement";
                            break;
                    }
                }
                return ["app", section, state.name.split(".").pop()].join("-");
            },
            showConnectedAccountsGlobalHook: true,
            showConnectedAccountsOnPageHook: false,
            showAvailableAppsIntro: false,
        },
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_appperformance: {
        parent: "digitalsuite_app",
        url: "/performance/:appId/:country/:duration/",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_appperformance",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_appperformance",
        configId: "AppPerformanceReport",
        overrideParams: {
            country: 999,
        },
        resolve: [resolvers.appInfo],
        pageId: {
            section: "apps",
            subSection: "overview",
            subSubSection: "performance",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Overview",
            subSubSection: "appPerformance",
        },
        data: {
            menuKbItems: null as any,
            pageViewTracking: null,
        },
        pageTitle: "mobileAppsAnalysis.overview.performance.titleNew",
        reloadOnSearch: false,
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_apprankinghistory: {
        parent: "digitalsuite_app",
        url: "/rankinghistory/:appId/:country/:duration?category&mode&categoryMode",
        params: {
            duration: "1m",
        },
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_apprankinghistory",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_apprankinghistory",
        configId: "AppRanks",
        resolve: [resolvers.appInfo],
        pageId: {
            section: "apps",
            subSection: "overview",
            subSubSection: "rankingHistory",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Overview",
            subSubSection: "rankingHistory",
        },
        data: {
            menuKbItems: null as any,
            pageViewTracking: null,
        },
        pageTitle: "mobileAppsAnalysis.overview.rankingHistory.title",
        defaultQueryParams: {
            duration: "1m",
            category: null as any,
            categoryMode: ERankingCategoryMode.ALL,
        },
        reloadOnSearch: false,
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_apprankingdaily: {
        parent: "digitalsuite_app",
        url: "/rankingdaily/:appId/:country?duration&category&mode&orderby",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_apprankingdaily",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_apprankingdaily",
        configId: "AppRanks",
        resolve: [resolvers.appInfo],
        pageId: {
            section: "apps",
            subSection: "overview",
            subSubSection: "rankingDaily",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Overview",
            subSubSection: "rankingDaily",
        },
        data: {
            menuKbItems: null as any,
            pageViewTracking: null,
        },
        pageTitle: "apps.overview.rankingDaily.title",
        defaultQueryParams: {
            duration: "1m",
            category: null as any,
        },
        reloadOnSearch: false,
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_usersengagement: {
        parent: "digitalsuite_app",
        configId: () => {
            const isApple = chosenItems[0]?.AppStore === APPLE_STORE;

            if (isApple) {
                const isNewEngagementIosMetrics = getIsNewEngagementIosMetrics();

                return isNewEngagementIosMetrics ? "AppEngagementUserActivityIOS" : "AppEngagementOverview";
            }

            return "AppEngagementOverview";
        },
        // see ProNavigator.ts > getConfigIdFromStateConfig method. Having configId as a
        // function causes the appropriate suffix to be added to it ie. IOS or Android
        url: !hasPremiumPlusClaim
            ? "/usersengagement/:appId/:country/:duration?comparedDuration?tab?timeGranularity"
            : "/usersengagement/:appId/:country/:duration?comparedDuration?tab?engagementTimeGranularity?usageTimeGranularity",
        onEnter: ($transition$) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_usersengagement",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        periodOverPeriodEnabled: true,
        stateNameForEductionBar: "companyresearch_app_usersengagement",
        permission: "MobEngagementOverview",
        resolve: [resolvers.appInfo],
        data: {
            pageViewTracking: null,
        },
        pageId: {
            section: "apps",
            subSection: "usage",
            subSubSection: "usersengagement",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Usage",
            subSubSection: "usersengagement",
        },
        icon: "sw-icon-overview",
        pageTitle: "apps.usage.usersengagement.title",
        defaultQueryParams: {
            timeGranularity: "Daily",
            tab: "MonthlyActiveUsers",
        },
        reloadOnSearch: false,
        orangeBadgeTitle: () => (getHasPremiumPlusClaim() ? "sidenav.new" : ""),
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_storeengagement: {
        parent: "digitalsuite_app",
        // see ProNavigator.ts > getConfigIdFromStateConfig method. Having configId as a
        // function causes the appropriate suffix to be added to it ie. IOS or Android
        configId: (toState: IRouterState, toParams: any) => {
            const isIos = toParams?.appId?.startsWith(AppStoreID.APPLE);
            return isIos ? "AppEngagementOverviewIOSDownloads" : "AppEngagementOverview";
        },
        configIdDeps: ["AppEngagementOverviewIOSDownloads", "AppEngagementOverviewIOS", "AppEngagementOverviewAndroid"],
        url: "/storeengagement/:appId/:country/:duration?tab?timeGranularity",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_storeengagement",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_storeengagement",
        permission: "MobEngagementOverview",
        resolve: [resolvers.appInfo],
        data: {
            pageViewTracking: null,
        },
        pageId: {
            section: "apps",
            subSection: "store",
            subSubSection: "storeengagement",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Store",
            subSubSection: "storeengagement",
        },
        icon: "sw-icon-overview",
        pageTitle: "apps.store.storeengagement.title",
        defaultQueryParams: {
            timeGranularity: "Daily",
            tab: "CurrentInstalls",
        },
        reloadOnSearch: false,
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_appretention: {
        parent: "digitalsuite_app",
        configId: "AppRetentionFront",
        url: "/retention/:appId/:country/:duration",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_appretention",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_appretention",
        resolve: [resolvers.appInfo],
        data: {
            pageViewTracking: null,
        },
        pageId: {
            section: "apps",
            subSection: "user",
            subSubSection: "retention",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/User",
            subSubSection: "retention",
        },
        icon: "sw-icon-audience",
        pageTitle: "app.intelligence.retention.title",
        pageSubtitle: "app.intelligence.retention.subTitle",
        orangeBadgeTitle: () => (getHasPremiumPlusClaim() ? "" : "sidenav.beta"),
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_appdmg: {
        parent: "digitalsuite_app",
        configId: "AppAudienceDemographics",
        url: "/demographics/:appId/:country/:duration",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_appdmg",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_appdmg",
        data: {
            pageViewTracking: null,
        },
        permission: "MobEngagementAudienceInterests",
        resolve: [resolvers.appInfo],
        pageId: {
            section: "apps",
            subSection: "audience",
            subSubSection: "demographics",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Audience",
            subSubSection: "demographics",
        },
        icon: "sw-icon-demographics",
        pageTitle: "apps.audience.demographics.pageTitle",
        orangeBadgeTitle: () => (getHasPremiumPlusClaim() ? "" : "sidenav.new"),
        homeState: "digitalsuite_appanalysis_home",
    },
    digitalsuite_app_appinterests: {
        parent: "digitalsuite_app",
        configId: "AppAudienceInterests",
        url: "/affinity/:appId/:country/:duration?orderby?page?filter?compare?category",
        onEnter: ($transition$: Transition) => {
            // Prevent the URL from changing
            $transition$.abort();
            logWithTransitionScope(
                $transition$,
                "onEnter",
                "INFO - competitionAppAnalysisConfig - digitalsuite_app_appinterests",
            );
            // Redirect to external site
            window.location.href = swSettings.swsites.apps;
        },
        stateNameForEductionBar: "companyresearch_app_appinterests",
        permission: "MobEngagementAudienceInterests",
        resolve: [resolvers.appInfo],
        data: {
            pageViewTracking: null,
        },
        pageId: {
            section: "apps",
            subSection: "audience",
            subSubSection: "affinity",
        },
        trackingId: {
            section: "App Intelligence",
            subSection: "App Analysis/Audience",
            subSubSection: "interests",
        },
        icon: "sw-icon-audience",
        pageTitle: "apps.engagementaffinity.pageTitle",
        pageSubtitle: "apps.engagementaffinity.subTitle",
        homeState: "digitalsuite_appanalysis_home",
        defaultQueryParams: {
            category: "",
        },
    },
};

export const competitionAppAnalysisConfig: RouterStateConfig = {
    ...swConfig,
};
